import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { announcementAPI } from '../../services/api';
import { Announcement } from '../../types';
import './AnnouncementManagement.css';

const AnnouncementManagement: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_published: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementAPI.getAllAdmin();
      setAnnouncements(response.data.announcements);
    } catch (error) {
      console.error('Duyuru yükleme hatası:', error);
      setError('Duyurular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (announcement?: Announcement) => {
    if (announcement) {
      setEditMode(true);
      setCurrentAnnouncement(announcement);
      setFormData({
        title: announcement.title,
        content: announcement.content,
        is_published: announcement.is_published,
      });
    } else {
      setEditMode(false);
      setCurrentAnnouncement(null);
      setFormData({
        title: '',
        content: '',
        is_published: true,
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      title: '',
      content: '',
      is_published: true,
    });
    setEditMode(false);
    setCurrentAnnouncement(null);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Başlık ve içerik gereklidir');
      return;
    }

    try {
      if (editMode && currentAnnouncement) {
        await announcementAPI.update(currentAnnouncement.id, formData);
        setSuccess('Duyuru başarıyla güncellendi');
      } else {
        await announcementAPI.create(formData);
        setSuccess('Duyuru başarıyla oluşturuldu');
      }

      setTimeout(() => {
        handleCloseModal();
        fetchAnnouncements();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu duyuruyu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await announcementAPI.delete(id);
      setSuccess('Duyuru başarıyla silindi');
      fetchAnnouncements();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Silme işlemi başarısız');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    try {
      await announcementAPI.update(announcement.id, {
        is_published: !announcement.is_published,
      });
      fetchAnnouncements();
    } catch (error) {
      console.error('Durum değiştirme hatası:', error);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar - Same as AdminDashboard */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>
            <i className="bi bi-shield-lock-fill me-2"></i>
            Admin Panel
          </h2>
        </div>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item">
            <i className="bi bi-speedometer2"></i>
            Dashboard
          </Link>
          <Link to="/admin/announcements" className="nav-item active">
            <i className="bi bi-megaphone"></i>
            Duyurular
          </Link>
          <Link to="/admin/gallery" className="nav-item">
            <i className="bi bi-images"></i>
            Galeri
          </Link>
          <Link to="/admin/faqs" className="nav-item">
            <i className="bi bi-question-circle"></i>
            SSS
          </Link>
          <Link to="/admin/users" className="nav-item">
            <i className="bi bi-people"></i>
            Kullanıcılar
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="btn btn-secondary w-100 mb-2">
            <i className="bi bi-house-door me-2"></i>
            Ana Sayfa
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>Duyuru Yönetimi</h1>
            <p className="text-muted">Duyuruları yönetin</p>
          </div>
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Yeni Duyuru
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {/* Announcements List */}
        <div className="dashboard-card">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                <p className="mt-3">Henüz duyuru yok</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Başlık</th>
                      <th>Slug</th>
                      <th>Yazar</th>
                      <th>Durum</th>
                      <th>Tarih</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((announcement) => (
                      <tr key={announcement.id}>
                        <td>{announcement.id}</td>
                        <td>
                          <div className="announcement-title">{announcement.title}</div>
                          <div className="announcement-preview">
                            {announcement.content.substring(0, 60)}...
                          </div>
                        </td>
                        <td>
                          <code className="slug-code">{announcement.slug}</code>
                        </td>
                        <td>
                          {announcement.author
                            ? `${announcement.author.name} ${announcement.author.surname}`
                            : 'Bilinmiyor'}
                        </td>
                        <td>
                          <button
                            onClick={() => handleToggleActive(announcement)}
                            className={`badge ${
                              announcement.is_published ? 'badge-success' : 'badge-secondary'
                            }`}
                          >
                            {announcement.is_published ? 'Yayında' : 'Taslak'}
                          </button>
                        </td>
                        <td>{new Date(announcement.created_at).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => handleOpenModal(announcement)}
                              className="btn-icon btn-icon-primary"
                              title="Düzenle"
                              aria-label="Düzenle duyuru"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(announcement.id)}
                              className="btn-icon btn-icon-danger"
                              title="Sil"
                              aria-label="Duyuruyu sil"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Duyuru Düzenle' : 'Yeni Duyuru Oluştur'}</h3>
              <button onClick={handleCloseModal} className="modal-close" title="Kapat" aria-label="Kapat">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="title">Başlık *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Duyuru başlığı"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">İçerik *</label>
                <textarea
                  id="content"
                  name="content"
                  className="form-control"
                  rows={8}
                  value={formData.content}
                  onChange={handleChange}
                  required
                  placeholder="Duyuru içeriği"
                />
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  className="form-check-input"
                  checked={formData.is_published}
                  onChange={handleChange}
                />
                <label htmlFor="is_published" className="form-check-label">
                  Yayınla
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="bi bi-save me-2"></i>
                  {editMode ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManagement;
